import { defineElement } from '../../core/routing.util';
import { URLInitOptions } from '../../core/routing';
import { SW_TARGET_UPDATE_EVENT, SW_ROUTER_LINK_TARGET_AVAILABLE_ATTRIBUTES } from './router-link.config';

/**
 * `RouterLinkTarget`: a NativeJS Web Component that contains link options for the `RouterLink` component. It allows
 * to resolve multiple options in one single link.
 *
 * In order to have multiple namespaces for a specific link, you must use the `RouterLinkTarget` component to create
 * a option for each single namespace that you have.
 *
 * You can describe the url you want to link using the following attributes:
 * - `slot="target"` - required, it will be appended to target slot inside the `<sw-router-link>` component
 * - `namespace` - required, every link option must be namespaced to some string
 * - `pathname` - the pathname you want to link out to
 * - `searchParams` - the search parameters you want to link out to
 * - `hash` - the hash string you want to link out to
 *
 *
 * Usage:
 *
 *     <sw-router-link>
 *       <sw-router-link-target slot="target" namespace="car-preview" path="classA"></sw-router-link-target>
 *       <sw-router-link-target slot="target" namespace="financial-calculator" path="classA"></sw-router-link-target>
 *       Check more
 *     </sw-router-link>
 *
 * @export
 * @class RouterLinkTarget
 * @extends {HTMLElement}
 */
export class RouterLinkTarget extends HTMLElement {
  /**
   * The dynamic attributes supported by the `RouterLinkTarget` web component.
   * - `namespace` - required, without it the link will never resolve, and wil behave as normal text
   * - `pathname` - the pathname of the target destination (e.g. `"/foo"`)
   * - `path` - the pathname of the target destination (e.g. `"/foo"`)
   * - `search-params` - the search parameters of the target destination (e.g. `"foo=bar"`)
   * - `hash` - the hash of the target destination (e.g. `"baz"`)
   *
   * @readonly
   * @static
   * @type {string[]}
   * @memberof RouterLinkTarget
   */
  static get observedAttributes(): string[] {
    return SW_ROUTER_LINK_TARGET_AVAILABLE_ATTRIBUTES;
  }

  /**
   * The `<sw-router-link-target>` component stylesheet. Mainly preventing
   * the element from rendering
   *
   * @readonly
   * @static
   * @type {string}
   * @memberof RouterLinktarget
   */
  private static stylesheet = `
    :host {
      display: none !important;
    }
  `;

  /**
   * The in-memory holder for all the URL parts received via attributes
   * @ignore
   * @public
   * @readonly
   * @type {URLInitOptions}
   * @memberof RouterLinkTarget
   */
  public readonly urlParts: URLInitOptions;

  /**
   * The unique identifier (key) of the router link target element
   * @ignore
   * @public
   * @readonly
   * @type {symbol}
   * @memberof RouterLinkTarget
   */
  public readonly targetKey: symbol;

  /**
   * The handler function that re-resolves the `targetURL` every time a `URLChangeEvent` is triggered
   * by the routing library.
   * @ignore
   * @private
   * @type {URLChangeListenerFunction}
   * @memberof RouterLink
   */
  constructor() {
    super();
    this.urlParts = {
      namespace: '',
      path: '',
      hash: '',
      searchParams: {},
    };
    this.targetKey = Symbol();

    // Prevent element from rendering
    const stylesheet = document.createElement('style');
    stylesheet.textContent = RouterLinkTarget.stylesheet;
    const shadowRoot = this.attachShadow({ mode: 'closed' });
    shadowRoot.append(stylesheet);
  }

  /**
   * The Web Component API function that is called every time an attribute in the
   * `observedAttributes` array is modified from the outsides.
   *
   * It dispatches an `URLOptionsChange` event to the parent element that will update
   * the internal representation of all attributes and recomputes the resolved URL
   * by using the routing library.
   *
   * @ignore
   * @param {string} name The name of the attribute that changed
   * @param {string} _oldValue The previous value of the attribute
   * @param {string} newValue The new value of the attribute
   * @memberof RouterLink
   */
  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    switch (name) {
      case 'path':
      case 'pathname':
        this.urlParts.path = newValue;
        break;
      case 'hash':
        this.urlParts.hash = newValue;
        break;
      case 'search-params': {
        const paramEntries = newValue.split('&').map((param) => param.split('='));
        const paramObject = Object.fromEntries(paramEntries);
        this.urlParts.searchParams = paramObject;
        break;
      }
      case 'namespace':
        this.urlParts.namespace = newValue;
        break;
    }

    this.dispatchEvent(
      new CustomEvent(SW_TARGET_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          urlParts: this.urlParts,
          key: this.targetKey,
        },
      }),
    );
  }
}

/**
 * Register the RouterLink in the browser's `customElements` registry with the given `tagName`.
 *
 * @export
 * @param {string} [tagName='sw-router-link-target'] The router link target tag name
 * @return {*}  {void}
 */
export function define(tagName = 'sw-router-link-target'): void {
  defineElement(RouterLinkTarget, tagName);
}
